// src/components/PortfolioAnalysisModal.js

import React, { useState, useEffect, useCallback } from 'react';
import axios from '../config/axiosConfig';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    Chip,
    LinearProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Grid,
    Divider,
    Tooltip,
    Alert,
    useMediaQuery,
} from '@mui/material';
import {
    Close as CloseIcon,
    ExpandMore as ExpandMoreIcon,
    AutoAwesome as AutoAwesomeIcon,
    Security as SecurityIcon,
    PieChart as PieChartIcon,
    TrendingUp as TrendingUpIcon,
    AccountBalance as AccountBalanceIcon,
    CalendarMonth as CalendarIcon,
    Psychology as PsychologyIcon,
    Lightbulb as LightbulbIcon,
    Refresh as RefreshIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    PictureAsPdf as PdfIcon,
    Lock as LockIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import jsPDF from 'jspdf';

const LOADING_STEPS = [
    'Loading portfolio data...',
    'Analyzing financials...',
    'Calculating risk metrics...',
    'Evaluating earnings & dividends...',
    'Generating AI insights...',
];

const PortfolioAnalysisModal = ({ open, onClose, portfolioId, portfolioName, userPlan }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isDark = theme.palette.mode === 'dark';

    const [analysis, setAnalysis] = useState(null);
    const [metadata, setMetadata] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingStep, setLoadingStep] = useState(0);
    const [error, setError] = useState(null);
    const isFreeUser = userPlan === 'Free';

    // ============ Theme-Aware Styles ============

    const cardBg = isDark
        ? 'rgba(255,255,255,0.04)'
        : 'rgba(0,0,0,0.02)';

    const cardBorder = isDark
        ? '1px solid rgba(255,255,255,0.08)'
        : '1px solid rgba(0,0,0,0.08)';

    const sectionCardSx = {
        p: isMobile ? 1.25 : 1.5,
        borderRadius: 1.5,
        bgcolor: cardBg,
        border: cardBorder,
        mb: 1.5,
    };

    const accordionSx = {
        bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
        boxShadow: 'none',
        border: cardBorder,
        borderRadius: '8px !important',
        overflow: 'hidden',
        '&:before': { display: 'none' },
        '&.Mui-expanded': { margin: 0 },
    };

    const chipSize = 'small';

    // ============ Data Fetching ============

    useEffect(() => {
        if (open && portfolioId) {
            fetchExistingAnalysis();
        }
        if (!open) {
            setError(null);
            setLoadingStep(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, portfolioId]);

    const fetchExistingAnalysis = async () => {
        try {
            const response = await axios.get(`/api/ai/portfolio-analysis/${portfolioId}`);
            if (response.data.hasAnalysis) {
                setAnalysis(response.data.analysis);
                setMetadata(response.data.metadata);
            } else {
                setAnalysis(null);
                setMetadata(null);
            }
        } catch (err) {
            console.error('Error fetching existing analysis:', err);
            if (err.response?.status === 403) {
                setError({
                    type: 'upgrade',
                    message: err.response.data.error || 'AI Portfolio Analysis requires a Basic plan or higher.',
                    upgradeUrl: err.response.data.upgradeUrl || '/pricing',
                });
            }
        }
    };

    const runAnalysis = useCallback(async (refresh = false) => {
        setLoading(true);
        setError(null);
        setLoadingStep(0);

        const stepInterval = setInterval(() => {
            setLoadingStep(prev => {
                if (prev < LOADING_STEPS.length - 1) return prev + 1;
                return prev;
            });
        }, 2500);

        try {
            const url = `/api/ai/portfolio-analysis/${portfolioId}${refresh ? '?refresh=true' : ''}`;
            const response = await axios.post(url);
            setAnalysis(response.data.analysis);
            setMetadata(response.data.metadata);
        } catch (err) {
            console.error('Analysis failed:', err);
            if (err.response?.status === 403) {
                setError({
                    type: 'upgrade',
                    message: err.response.data.error || 'AI Portfolio Analysis requires a Basic plan or higher.',
                    upgradeUrl: err.response.data.upgradeUrl || '/pricing',
                });
            } else if (err.response?.status === 402) {
                setError({
                    type: 'credits',
                    message: err.response.data.error,
                    creditsRemaining: err.response.data.creditsRemaining,
                    creditsRequired: err.response.data.creditsRequired,
                });
            } else {
                setError({
                    type: 'general',
                    message: err.response?.data?.error || 'Analysis failed. Please try again.',
                });
            }
        } finally {
            clearInterval(stepInterval);
            setLoading(false);
        }
    }, [portfolioId]);

    // ============ Helpers ============

    const getHealthColor = (score) => {
        if (score >= 80) return theme.palette.success.main;
        if (score >= 60) return theme.palette.info.main;
        if (score >= 40) return theme.palette.warning.main;
        return theme.palette.error.main;
    };

    const getSeverityIcon = (severity) => {
        const size = isMobile ? 16 : 18;
        switch (severity) {
            case 'Low': return <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: size }} />;
            case 'Medium': return <WarningIcon sx={{ color: theme.palette.warning.main, fontSize: size }} />;
            case 'High': return <ErrorIcon sx={{ color: theme.palette.error.main, fontSize: size }} />;
            default: return null;
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'High': return 'error';
            case 'Medium': return 'warning';
            case 'Low': return 'info';
            default: return 'default';
        }
    };

    const getVerdictColor = (verdict) => {
        switch (verdict) {
            case 'Strong': return theme.palette.success.main;
            case 'Positive': return theme.palette.success.light;
            case 'Neutral': return theme.palette.text.secondary;
            case 'Concerning': return theme.palette.warning.main;
            case 'Weak': return theme.palette.error.main;
            default: return theme.palette.text.primary;
        }
    };

    // ============ PDF Export ============

    const exportToPDF = () => {
        if (!analysis) return;

        const doc = new jsPDF('p', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 18;
        const contentWidth = pageWidth - margin * 2;
        let y = margin;

        const colors = {
            brand: [0, 127, 255],
            headerBg: [10, 25, 41],
            title: [26, 32, 39],
            body: [62, 80, 96],
            light: [140, 155, 170],
            success: [14, 179, 91],
            warning: [237, 108, 2],
            error: [244, 67, 54],
            info: [2, 136, 209],
            divider: [220, 225, 230],
            cardBg: [245, 247, 250],
            white: [255, 255, 255],
        };

        const getScoreColor = (score) => {
            if (score >= 80) return colors.success;
            if (score >= 60) return colors.info;
            if (score >= 40) return colors.warning;
            return colors.error;
        };

        const getSevColor = (severity) => {
            if (severity === 'High') return colors.error;
            if (severity === 'Medium') return colors.warning;
            return colors.success;
        };

        const checkPageBreak = (needed = 20) => {
            if (y + needed > pageHeight - 20) {
                doc.addPage();
                y = margin;
            }
        };

        // ---- Header Banner ----
        doc.setFillColor(...colors.headerBg);
        doc.rect(0, 0, pageWidth, 42, 'F');
        doc.setFillColor(...colors.brand);
        doc.rect(0, 42, pageWidth, 1.5, 'F');

        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 140, 180);
        doc.text('MarketRodeo \u2014 RodeoAI', margin, 12);

        doc.setFontSize(20);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...colors.white);
        doc.text(portfolioName, margin, 26);

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(160, 185, 210);
        doc.text('AI Portfolio Analysis Report', margin, 34);

        if (metadata?.analyzedAt) {
            const dateStr = new Date(metadata.analyzedAt).toLocaleDateString('en-US', {
                year: 'numeric', month: 'long', day: 'numeric'
            });
            doc.setFontSize(9);
            doc.setTextColor(140, 165, 190);
            doc.text(dateStr, pageWidth - margin, 34, { align: 'right' });
        }

        y = 52;

        // ---- Summary Metrics Row ----
        if (analysis.overview) {
            const metricsData = [
                { label: 'HEALTH SCORE', value: `${analysis.overview.healthScore}/100`, color: getScoreColor(analysis.overview.healthScore) },
                { label: 'RISK LEVEL', value: analysis.riskAssessment?.riskLevel || 'N/A', color: analysis.riskAssessment?.riskLevel === 'Low' ? colors.success : analysis.riskAssessment?.riskLevel === 'Moderate' ? colors.info : colors.error },
                { label: 'DIVERSIFICATION', value: analysis.diversification?.score != null ? `${analysis.diversification.score}/100` : 'N/A', color: analysis.diversification?.score >= 70 ? colors.success : analysis.diversification?.score >= 40 ? colors.warning : colors.error },
                { label: 'SENTIMENT', value: analysis.analystSentiment?.overallSentiment || 'N/A', color: analysis.analystSentiment?.overallSentiment?.includes('Bullish') ? colors.success : analysis.analystSentiment?.overallSentiment?.includes('Bearish') ? colors.error : colors.body },
            ];

            const boxWidth = (contentWidth - 6) / 4;
            metricsData.forEach((m, i) => {
                const x = margin + i * (boxWidth + 2);
                doc.setFillColor(...colors.cardBg);
                doc.roundedRect(x, y, boxWidth, 18, 2, 2, 'F');
                doc.setDrawColor(...colors.divider);
                doc.roundedRect(x, y, boxWidth, 18, 2, 2, 'S');

                doc.setFontSize(6.5);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(...colors.light);
                doc.text(m.label, x + boxWidth / 2, y + 7, { align: 'center' });

                doc.setFontSize(11);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(...m.color);
                doc.text(m.value, x + boxWidth / 2, y + 14, { align: 'center' });
            });
            y += 24;
        }

        // ---- Helpers ----
        const addSectionHeader = (title) => {
            checkPageBreak(18);
            y += 2;
            doc.setFontSize(13);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...colors.title);
            doc.text(title, margin, y);
            y += 2;
            doc.setDrawColor(...colors.brand);
            doc.setLineWidth(0.8);
            doc.line(margin, y, margin + 35, y);
            y += 6;
        };

        const addText = (text, options = {}) => {
            const { bold = false, size = 9.5, color = colors.body, indent = 0, spacing = 4.5 } = options;
            doc.setFontSize(size);
            doc.setFont('helvetica', bold ? 'bold' : 'normal');
            doc.setTextColor(...color);
            const lines = doc.splitTextToSize(text, contentWidth - indent);
            checkPageBreak(lines.length * spacing);
            doc.text(lines, margin + indent, y);
            y += lines.length * spacing;
        };

        const addBullet = (text, indent = 4) => {
            doc.setFontSize(9);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...colors.body);
            doc.text('\u2022', margin + indent, y);
            const lines = doc.splitTextToSize(text, contentWidth - indent - 5);
            checkPageBreak(lines.length * 4.5);
            doc.text(lines, margin + indent + 4, y);
            y += lines.length * 4.5;
        };

        const addKeyValue = (key, value, valueColor = colors.body) => {
            checkPageBreak(8);
            doc.setFontSize(9);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...colors.title);
            doc.text(`${key}:`, margin + 2, y);
            const kw = doc.getTextWidth(`${key}: `);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...valueColor);
            doc.text(String(value), margin + 2 + kw + 1, y);
            y += 5.5;
        };

        // ---- Overview ----
        if (analysis.overview) {
            addSectionHeader('Portfolio Overview');
            addKeyValue('Health', `${analysis.overview.healthScore}/100 \u2014 ${analysis.overview.healthLabel}`, getScoreColor(analysis.overview.healthScore));
            addText(analysis.overview.summary);
            y += 3;
        }

        // ---- Risk Assessment ----
        if (analysis.riskAssessment) {
            addSectionHeader('Risk Assessment');
            const rl = analysis.riskAssessment.riskLevel;
            addKeyValue('Risk Level', rl, rl === 'Low' ? colors.success : rl === 'Moderate' ? colors.info : colors.error);
            addText(analysis.riskAssessment.summary);
            y += 2;
            if (analysis.riskAssessment.keyRisks) {
                analysis.riskAssessment.keyRisks.forEach(risk => {
                    checkPageBreak(14);
                    doc.setFillColor(...getSevColor(risk.severity));
                    doc.circle(margin + 5, y - 1, 1.2, 'F');
                    addText(`${risk.risk} [${risk.severity}]`, { bold: true, indent: 9, size: 9.5, color: colors.title });
                    addText(risk.detail, { indent: 9, size: 9 });
                    y += 1;
                });
            }
            y += 3;
        }

        // ---- Diversification ----
        if (analysis.diversification) {
            addSectionHeader('Diversification');
            const ds = analysis.diversification.score;
            addKeyValue('Score', `${ds}/100`, ds >= 70 ? colors.success : ds >= 40 ? colors.warning : colors.error);
            addText(analysis.diversification.summary);
            if (analysis.diversification.sectorAnalysis) {
                y += 1;
                addText(`Sector: ${analysis.diversification.sectorAnalysis}`, { indent: 2, size: 9 });
            }
            if (analysis.diversification.correlationInsight) {
                addText(`Correlation: ${analysis.diversification.correlationInsight}`, { indent: 2, size: 9 });
            }
            if (analysis.diversification.suggestions?.length > 0) {
                y += 1;
                analysis.diversification.suggestions.forEach(s => addBullet(s));
            }
            y += 3;
        }

        // ---- Holdings Analysis ----
        if (analysis.holdingsAnalysis?.length > 0) {
            addSectionHeader('Holdings Analysis');
            analysis.holdingsAnalysis.forEach(h => {
                checkPageBreak(18);
                const vc = h.verdict === 'Strong' || h.verdict === 'Positive' ? colors.success :
                    h.verdict === 'Concerning' ? colors.warning :
                        h.verdict === 'Weak' ? colors.error : colors.body;

                doc.setFontSize(10);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(...colors.title);
                doc.text(h.symbol, margin + 2, y);
                const sw = doc.getTextWidth(h.symbol);
                doc.setFontSize(8);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(...vc);
                doc.text(` \u2014 ${h.verdict}`, margin + 2 + sw, y);
                y += 5;

                if (h.financialHealth) addText(h.financialHealth, { indent: 4, size: 8.5 });
                if (h.highlights) h.highlights.forEach(hl => addBullet(hl, 4));
                y += 2;
            });
            y += 2;
        }

        // ---- Dividend Analysis ----
        if (analysis.dividendAnalysis) {
            addSectionHeader('Dividend Analysis');
            if (analysis.dividendAnalysis.totalAnnualIncome != null)
                addKeyValue('Annual Income', `$${analysis.dividendAnalysis.totalAnnualIncome.toLocaleString()}`);
            if (analysis.dividendAnalysis.portfolioYield != null)
                addKeyValue('Portfolio Yield', `${analysis.dividendAnalysis.portfolioYield.toFixed(2)}%`);
            addText(analysis.dividendAnalysis.summary);
            if (analysis.dividendAnalysis.highlights)
                analysis.dividendAnalysis.highlights.forEach(h => addBullet(h));
            y += 3;
        }

        // ---- Earnings ----
        if (analysis.earningsOutlook) {
            addSectionHeader('Earnings Outlook');
            addText(analysis.earningsOutlook.summary);
            if (analysis.earningsOutlook.recentBeats?.length > 0)
                addKeyValue('Recent Beats', analysis.earningsOutlook.recentBeats.join(', '), colors.success);
            if (analysis.earningsOutlook.recentMisses?.length > 0)
                addKeyValue('Recent Misses', analysis.earningsOutlook.recentMisses.join(', '), colors.error);
            if (analysis.earningsOutlook.upcoming?.length > 0)
                addKeyValue('Upcoming', analysis.earningsOutlook.upcoming.join(', '));
            y += 3;
        }

        // ---- Analyst Sentiment ----
        if (analysis.analystSentiment) {
            addSectionHeader('Analyst Sentiment');
            const sc = analysis.analystSentiment.overallSentiment?.includes('Bullish') ? colors.success :
                analysis.analystSentiment.overallSentiment?.includes('Bearish') ? colors.error : colors.body;
            addKeyValue('Overall', analysis.analystSentiment.overallSentiment, sc);
            addText(analysis.analystSentiment.summary);
            if (analysis.analystSentiment.mostBullish?.length > 0)
                addKeyValue('Most Bullish', analysis.analystSentiment.mostBullish.join(', '), colors.success);
            if (analysis.analystSentiment.mostBearish?.length > 0)
                addKeyValue('Most Bearish', analysis.analystSentiment.mostBearish.join(', '), colors.error);
            y += 3;
        }

        // ---- Actionable Insights ----
        if (analysis.actionableInsights?.length > 0) {
            addSectionHeader('Actionable Insights');
            analysis.actionableInsights.forEach((insight, i) => {
                checkPageBreak(16);
                const pc = insight.priority === 'High' ? colors.error :
                    insight.priority === 'Medium' ? colors.warning : colors.info;

                doc.setFillColor(...pc);
                doc.roundedRect(margin + 2, y - 3.5, 1.5, 12, 0.5, 0.5, 'F');

                doc.setFontSize(9.5);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(...colors.title);
                doc.text(insight.title, margin + 7, y);
                const tw = doc.getTextWidth(`${insight.title} `);
                doc.setFontSize(7.5);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(...pc);
                doc.text(`${insight.priority} \u00B7 ${insight.category}`, margin + 7 + tw, y);
                y += 5;
                addText(insight.detail, { indent: 7, size: 9 });
                y += 2;
            });
        }

        // ---- Footer on all pages ----
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setDrawColor(...colors.divider);
            doc.setLineWidth(0.3);
            doc.line(margin, pageHeight - 14, pageWidth - margin, pageHeight - 14);

            doc.setFontSize(7.5);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...colors.light);
            doc.text('Generated by MarketRodeo RodeoAI', margin, pageHeight - 9);

            doc.setFontSize(6.5);
            doc.setFont('helvetica', 'italic');
            doc.setTextColor(180, 180, 180);
            doc.text('AI-generated analysis \u2014 not financial advice.', pageWidth / 2, pageHeight - 9, { align: 'center' });

            doc.setFontSize(7.5);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...colors.light);
            doc.text(`${i} / ${pageCount}`, pageWidth - margin, pageHeight - 9, { align: 'right' });
        }

        doc.save(`${portfolioName}_AI_Analysis.pdf`);
    };

    // ============ Render Functions ============

    const renderLoadingState = () => (
        <Box sx={{ py: isMobile ? 6 : 8, px: isMobile ? 2 : 4, textAlign: 'center' }}>
            <AutoAwesomeIcon sx={{
                fontSize: isMobile ? 40 : 48, color: theme.palette.primary.main, mb: 2,
                animation: 'pulse 1.5s infinite',
                '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.5 } },
            }} />
            <Typography variant={isMobile ? 'body1' : 'h6'} fontWeight={600} gutterBottom>
                Analyzing {portfolioName}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{LOADING_STEPS[loadingStep]}</Typography>
            <LinearProgress variant="determinate" value={((loadingStep + 1) / LOADING_STEPS.length) * 100}
                sx={{ height: 6, borderRadius: 3, maxWidth: 400, mx: 'auto' }} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                This may take 15-30 seconds
            </Typography>
        </Box>
    );

    const renderEmptyState = () => {
        if (isFreeUser) {
            return (
                <Box sx={{ py: isMobile ? 6 : 8, px: isMobile ? 2 : 4, textAlign: 'center' }}>
                    <LockIcon sx={{ fontSize: 48, color: theme.palette.warning.main, mb: 2 }} />
                    <Typography variant="h6" fontWeight={600} gutterBottom>Premium Feature</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                        AI Portfolio Analysis requires a Basic plan or higher. Upgrade to unlock.
                    </Typography>
                    <Button variant="contained" href="/pricing" sx={{ textTransform: 'none', fontWeight: 600 }}>
                        Upgrade Plan
                    </Button>
                </Box>
            );
        }

        return (
            <Box sx={{ py: isMobile ? 6 : 8, px: isMobile ? 2 : 4, textAlign: 'center' }}>
                <AutoAwesomeIcon sx={{ fontSize: isMobile ? 44 : 56, color: theme.palette.primary.main, mb: 2 }} />
                <Typography variant={isMobile ? 'body1' : 'h6'} fontWeight={600} gutterBottom>AI Portfolio Analysis</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 450, mx: 'auto' }}>
                    Get a comprehensive AI-powered analysis of your portfolio including risk assessment,
                    diversification insights, earnings outlook, and actionable recommendations.
                </Typography>
                <Button variant="contained" size={isMobile ? 'medium' : 'large'} startIcon={<AutoAwesomeIcon />}
                    onClick={() => runAnalysis(false)} sx={{ textTransform: 'none', fontWeight: 600, px: 4 }}>
                    Analyze Portfolio
                </Button>
            </Box>
        );
    };

    const renderError = () => (
        <Box sx={{ py: isMobile ? 4 : 6, px: isMobile ? 2 : 4, textAlign: 'center' }}>
            {error.type === 'upgrade' ? (
                <>
                    <LockIcon sx={{ fontSize: 48, color: theme.palette.warning.main, mb: 2 }} />
                    <Typography variant="h6" fontWeight={600} gutterBottom>Premium Feature</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>{error.message}</Typography>
                    <Button variant="contained" href={error.upgradeUrl || '/pricing'} sx={{ textTransform: 'none', fontWeight: 600 }}>Upgrade Plan</Button>
                </>
            ) : error.type === 'credits' ? (
                <>
                    <Typography variant="h6" gutterBottom>Insufficient Credits</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {userPlan === 'Premium'
                            ? "You've used all your credits for this month. Credits reset at the start of your next billing cycle."
                            : `Portfolio analysis costs 5 credits. Upgrade your plan to get more credits.`
                        }
                    </Typography>
                    {userPlan === 'Premium' ? (
                        <Button variant="outlined" onClick={onClose} sx={{ textTransform: 'none' }}>
                            Got it
                        </Button>
                    ) : (
                        <Button variant="contained" href="/pricing" sx={{ textTransform: 'none', fontWeight: 600 }}>
                            Upgrade for More Credits
                        </Button>
                    )}
                </>
            ) : (
                <>
                    <ErrorIcon sx={{ fontSize: 48, color: theme.palette.error.main, mb: 2 }} />
                    <Typography variant="h6" gutterBottom>Analysis Failed</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{error.message}</Typography>
                    <Button variant="outlined" onClick={() => runAnalysis(true)} sx={{ textTransform: 'none' }}>Try Again</Button>
                </>
            )}
        </Box>
    );

    const renderSummaryMetrics = () => {
        const { overview, riskAssessment, diversification, analystSentiment } = analysis;
        if (!overview) return null;
        const metrics = [
            { label: 'Health', value: `${overview.healthScore}`, sub: overview.healthLabel, color: getHealthColor(overview.healthScore) },
            ...(riskAssessment ? [{ label: 'Risk', value: riskAssessment.riskLevel, color: riskAssessment.riskLevel === 'Low' ? theme.palette.success.main : riskAssessment.riskLevel === 'Moderate' ? theme.palette.info.main : theme.palette.error.main }] : []),
            ...(diversification?.score != null ? [{ label: 'Diversification', value: `${diversification.score}`, sub: '/100', color: diversification.score >= 70 ? theme.palette.success.main : diversification.score >= 40 ? theme.palette.warning.main : theme.palette.error.main }] : []),
            ...(analystSentiment?.overallSentiment ? [{ label: 'Sentiment', value: analystSentiment.overallSentiment }] : []),
        ];
        return (
            <Box sx={{ display: 'flex', gap: isMobile ? 1 : 2, mb: 2, flexWrap: 'wrap' }}>
                {metrics.map((m, i) => (
                    <Box key={i} sx={{
                        flex: isMobile ? '1 1 calc(50% - 8px)' : '1 1 0', minWidth: isMobile ? 'calc(50% - 8px)' : 0,
                        textAlign: 'center', p: isMobile ? 1 : 1.5, borderRadius: 1.5, bgcolor: cardBg, border: cardBorder,
                    }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>{m.label}</Typography>
                        <Typography variant={isMobile ? 'body1' : 'h6'} fontWeight="bold" sx={{ color: m.color || 'text.primary', lineHeight: 1.2 }}>
                            {m.value}{m.sub && <Typography component="span" variant="caption" color="text.secondary"> {m.sub}</Typography>}
                        </Typography>
                    </Box>
                ))}
            </Box>
        );
    };

    const renderOverview = () => {
        const { overview } = analysis;
        if (!overview) return null;
        return (
            <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', gap: 2, mb: 2, flexDirection: isMobile ? 'column' : 'row' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: isMobile ? '100%' : 'auto' }}>
                        <Box sx={{
                            width: isMobile ? 56 : 72, height: isMobile ? 56 : 72, borderRadius: '50%',
                            border: `4px solid ${getHealthColor(overview.healthScore)}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                            <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="bold" sx={{ color: getHealthColor(overview.healthScore) }}>{overview.healthScore}</Typography>
                        </Box>
                        {isMobile && <Typography variant="body1" fontWeight="bold">Portfolio Health: {overview.healthLabel}</Typography>}
                    </Box>
                    <Box>
                        {!isMobile && <Typography variant="h6" fontWeight="bold">Portfolio Health: {overview.healthLabel}</Typography>}
                        <Typography variant="body2" color="text.secondary">{overview.summary}</Typography>
                    </Box>
                </Box>
            </Box>
        );
    };

    const renderRiskAssessment = () => {
        const { riskAssessment } = analysis;
        if (!riskAssessment) return null;
        return (
            <Accordion defaultExpanded sx={accordionSx}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <SecurityIcon sx={{ mr: 1, color: theme.palette.warning.main, fontSize: isMobile ? 20 : 24 }} />
                    <Typography fontWeight="bold" sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}>Risk Assessment</Typography>
                    <Chip label={riskAssessment.riskLevel} size={chipSize} sx={{ ml: 1 }}
                        color={riskAssessment.riskLevel === 'Low' ? 'success' : riskAssessment.riskLevel === 'Moderate' ? 'info' : riskAssessment.riskLevel === 'High' ? 'warning' : 'error'} />
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{riskAssessment.summary}</Typography>
                    {riskAssessment.keyRisks?.map((risk, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
                            {getSeverityIcon(risk.severity)}
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight="bold">{risk.risk}</Typography>
                                <Typography variant="body2" color="text.secondary">{risk.detail}</Typography>
                            </Box>
                        </Box>
                    ))}
                </AccordionDetails>
            </Accordion>
        );
    };

    const renderDiversification = () => {
        const { diversification } = analysis;
        if (!diversification) return null;
        return (
            <Accordion sx={accordionSx}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <PieChartIcon sx={{ mr: 1, color: theme.palette.info.main, fontSize: isMobile ? 20 : 24 }} />
                    <Typography fontWeight="bold" sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}>Diversification</Typography>
                    {diversification.score != null && <Chip label={`${diversification.score}/100`} size={chipSize} sx={{ ml: 1 }} />}
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{diversification.summary}</Typography>
                    {diversification.sectorAnalysis && (<Box sx={sectionCardSx}><Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>Sector Analysis</Typography><Typography variant="body2" color="text.secondary">{diversification.sectorAnalysis}</Typography></Box>)}
                    {diversification.correlationInsight && (<Box sx={sectionCardSx}><Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>Correlation Insight</Typography><Typography variant="body2" color="text.secondary">{diversification.correlationInsight}</Typography></Box>)}
                    {diversification.suggestions?.length > 0 && (<Box sx={{ mt: 1 }}><Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>Suggestions</Typography>{diversification.suggestions.map((s, i) => (<Typography key={i} variant="body2" color="text.secondary" sx={{ pl: 1, mb: 0.5 }}>• {s}</Typography>))}</Box>)}
                </AccordionDetails>
            </Accordion>
        );
    };

    const renderHoldingsAnalysis = () => {
        const { holdingsAnalysis } = analysis;
        if (!holdingsAnalysis?.length) return null;
        return (
            <Accordion sx={accordionSx}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <TrendingUpIcon sx={{ mr: 1, color: theme.palette.success.main, fontSize: isMobile ? 20 : 24 }} />
                    <Typography fontWeight="bold" sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}>Holdings Analysis</Typography>
                    <Chip label={`${holdingsAnalysis.length} holdings`} size={chipSize} sx={{ ml: 1 }} />
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                    {holdingsAnalysis.map((holding, i) => (
                        <Box key={i} sx={sectionCardSx}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="body2" fontWeight="bold">{holding.symbol}</Typography>
                                <Chip label={holding.verdict} size="small" sx={{ color: getVerdictColor(holding.verdict), borderColor: getVerdictColor(holding.verdict), fontWeight: 600, fontSize: '0.7rem' }} variant="outlined" />
                            </Box>
                            {holding.financialHealth && <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: isMobile ? '0.8rem' : '0.875rem' }}>{holding.financialHealth}</Typography>}
                            {holding.highlights?.map((h, j) => (<Typography key={j} variant="body2" color="text.secondary" sx={{ pl: 1, fontSize: '0.8rem' }}>• {h}</Typography>))}
                        </Box>
                    ))}
                </AccordionDetails>
            </Accordion>
        );
    };

    const renderDividendAnalysis = () => {
        const { dividendAnalysis } = analysis;
        if (!dividendAnalysis) return null;
        return (
            <Accordion sx={accordionSx}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <AccountBalanceIcon sx={{ mr: 1, color: theme.palette.primary.main, fontSize: isMobile ? 20 : 24 }} />
                    <Typography fontWeight="bold" sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}>Dividend Analysis</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                    {(dividendAnalysis.totalAnnualIncome != null || dividendAnalysis.portfolioYield != null) && (
                        <Grid container spacing={isMobile ? 1 : 2} sx={{ mb: 2 }}>
                            {dividendAnalysis.totalAnnualIncome != null && (<Grid item xs={6}><Box sx={{ textAlign: 'center', ...sectionCardSx, mb: 0 }}><Typography variant="caption" color="text.secondary">Annual Income</Typography><Typography variant={isMobile ? 'body1' : 'h6'} fontWeight="bold">${dividendAnalysis.totalAnnualIncome.toLocaleString()}</Typography></Box></Grid>)}
                            {dividendAnalysis.portfolioYield != null && (<Grid item xs={6}><Box sx={{ textAlign: 'center', ...sectionCardSx, mb: 0 }}><Typography variant="caption" color="text.secondary">Portfolio Yield</Typography><Typography variant={isMobile ? 'body1' : 'h6'} fontWeight="bold">{dividendAnalysis.portfolioYield.toFixed(2)}%</Typography></Box></Grid>)}
                        </Grid>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{dividendAnalysis.summary}</Typography>
                    {dividendAnalysis.highlights?.map((h, i) => (<Typography key={i} variant="body2" color="text.secondary" sx={{ pl: 1, mb: 0.5 }}>• {h}</Typography>))}
                </AccordionDetails>
            </Accordion>
        );
    };

    const renderEarningsOutlook = () => {
        const { earningsOutlook } = analysis;
        if (!earningsOutlook) return null;
        return (
            <Accordion sx={accordionSx}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <CalendarIcon sx={{ mr: 1, color: theme.palette.secondary.main, fontSize: isMobile ? 20 : 24 }} />
                    <Typography fontWeight="bold" sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}>Earnings Outlook</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{earningsOutlook.summary}</Typography>
                    <Grid container spacing={isMobile ? 1 : 2}>
                        {earningsOutlook.recentBeats?.length > 0 && (<Grid item xs={12} sm={4}><Typography variant="body2" fontWeight="bold" sx={{ color: theme.palette.success.main, mb: 0.5 }}>Recent Beats</Typography>{earningsOutlook.recentBeats.map((s, i) => (<Chip key={i} label={s} size="small" sx={{ mr: 0.5, mb: 0.5 }} color="success" variant="outlined" />))}</Grid>)}
                        {earningsOutlook.recentMisses?.length > 0 && (<Grid item xs={12} sm={4}><Typography variant="body2" fontWeight="bold" sx={{ color: theme.palette.error.main, mb: 0.5 }}>Recent Misses</Typography>{earningsOutlook.recentMisses.map((s, i) => (<Chip key={i} label={s} size="small" sx={{ mr: 0.5, mb: 0.5 }} color="error" variant="outlined" />))}</Grid>)}
                        {earningsOutlook.upcoming?.length > 0 && (<Grid item xs={12} sm={4}><Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>Upcoming</Typography>{earningsOutlook.upcoming.map((s, i) => (<Chip key={i} label={s} size="small" sx={{ mr: 0.5, mb: 0.5 }} variant="outlined" />))}</Grid>)}
                    </Grid>
                </AccordionDetails>
            </Accordion>
        );
    };

    const renderAnalystSentiment = () => {
        const { analystSentiment } = analysis;
        if (!analystSentiment) return null;
        return (
            <Accordion sx={accordionSx}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <PsychologyIcon sx={{ mr: 1, color: theme.palette.info.main, fontSize: isMobile ? 20 : 24 }} />
                    <Typography fontWeight="bold" sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}>Analyst Sentiment</Typography>
                    {analystSentiment.overallSentiment && (<Chip label={analystSentiment.overallSentiment} size={chipSize} sx={{ ml: 1 }} color={analystSentiment.overallSentiment.includes('Bullish') ? 'success' : analystSentiment.overallSentiment.includes('Bearish') ? 'error' : 'default'} />)}
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{analystSentiment.summary}</Typography>
                    <Grid container spacing={isMobile ? 1 : 2}>
                        {analystSentiment.mostBullish?.length > 0 && (<Grid item xs={6}><Typography variant="body2" fontWeight="bold" sx={{ color: theme.palette.success.main, mb: 0.5 }}>Most Bullish</Typography>{analystSentiment.mostBullish.map((s, i) => (<Chip key={i} label={s} size="small" sx={{ mr: 0.5, mb: 0.5 }} color="success" variant="outlined" />))}</Grid>)}
                        {analystSentiment.mostBearish?.length > 0 && (<Grid item xs={6}><Typography variant="body2" fontWeight="bold" sx={{ color: theme.palette.error.main, mb: 0.5 }}>Most Bearish</Typography>{analystSentiment.mostBearish.map((s, i) => (<Chip key={i} label={s} size="small" sx={{ mr: 0.5, mb: 0.5 }} color="error" variant="outlined" />))}</Grid>)}
                    </Grid>
                </AccordionDetails>
            </Accordion>
        );
    };

    const renderActionableInsights = () => {
        const { actionableInsights } = analysis;
        if (!actionableInsights?.length) return null;
        return (
            <Accordion defaultExpanded sx={accordionSx}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <LightbulbIcon sx={{ mr: 1, color: theme.palette.warning.main, fontSize: isMobile ? 20 : 24 }} />
                    <Typography fontWeight="bold" sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}>Actionable Insights</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                    {actionableInsights.map((insight, i) => (
                        <Box key={i} sx={{
                            mb: 1.5, p: isMobile ? 1.25 : 1.5, borderRadius: 1.5,
                            bgcolor: cardBg, border: cardBorder,
                            borderLeft: `4px solid ${theme.palette[getPriorityColor(insight.priority)]?.main || theme.palette.grey[400]}`,
                        }}>
                            <Box sx={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', gap: 0.75, mb: 0.5, flexWrap: 'wrap' }}>
                                <Chip label={insight.priority} size="small" color={getPriorityColor(insight.priority)} sx={{ fontSize: '0.7rem', height: 22 }} />
                                <Chip label={insight.category} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 22 }} />
                                <Typography variant="body2" fontWeight="bold" sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>{insight.title}</Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>{insight.detail}</Typography>
                        </Box>
                    ))}
                </AccordionDetails>
            </Accordion>
        );
    };

    const renderAnalysis = () => (
        <Box>
            {metadata && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {metadata.analyzedAt && <Typography variant="caption" color="text.secondary">Analyzed: {new Date(metadata.analyzedAt).toLocaleString()}</Typography>}
                        {metadata.ageHours != null && metadata.ageHours > 12 && (
                            <Tooltip title="Analysis may be outdated. Consider re-running."><WarningIcon sx={{ fontSize: 16, color: theme.palette.warning.main }} /></Tooltip>
                        )}
                    </Box>
                    <Tooltip title="Re-run analysis">
                        <IconButton size="small" onClick={() => runAnalysis(true)} disabled={loading}><RefreshIcon fontSize="small" /></IconButton>
                    </Tooltip>
                </Box>
            )}
            {renderSummaryMetrics()}
            {renderOverview()}
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {renderActionableInsights()}
                {renderRiskAssessment()}
                {renderDiversification()}
                {renderHoldingsAnalysis()}
                {renderDividendAnalysis()}
                {renderEarningsOutlook()}
                {renderAnalystSentiment()}
            </Box>
            <Alert severity="info" sx={{ mt: 3, fontSize: '0.75rem' }}>
                This analysis is AI-generated and should not be considered financial advice. Always do your own research before making investment decisions.
            </Alert>
        </Box>
    );

    return (
        <Dialog
            open={open}
            onClose={loading ? undefined : onClose}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
            scroll="paper"
            PaperProps={{
                sx: {
                    // CRITICAL: Override global MuiPaper transparent background
                    backgroundColor: isDark
                        ? '#0A1929'
                        : '#ffffff',
                    backgroundImage: isDark
                        ? 'radial-gradient(50% 50% at 50% 0%, #132f4c 0%, #0A1929 100%)'
                        : 'none',
                    backdropFilter: 'none',
                    border: isDark
                        ? '1px solid rgba(255,255,255,0.1)'
                        : '1px solid rgba(0,0,0,0.12)',
                    ...(!isMobile && {
                        ...(analysis && !error && { minHeight: '60vh' }),
                        maxHeight: '90vh',
                        borderRadius: 2,
                    }),
                    ...(isMobile && { borderRadius: 0 }),
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                pb: 1, px: isMobile ? 2 : 3,
                borderBottom: cardBorder,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <AutoAwesomeIcon sx={{ color: theme.palette.primary.main, fontSize: isMobile ? 20 : 24 }} />
                    <Typography variant={isMobile ? 'body1' : 'h6'} fontWeight="bold" noWrap>Portfolio Analysis - {portfolioName}</Typography>
                </Box>
                <IconButton onClick={onClose} disabled={loading} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ px: isMobile ? 1.5 : 3 }}>
                {loading && renderLoadingState()}
                {!loading && error && renderError()}
                {!loading && !error && !analysis && renderEmptyState()}
                {!loading && !error && analysis && renderAnalysis()}
            </DialogContent>

            {!loading && analysis && (
                <DialogActions sx={{ px: isMobile ? 2 : 3, py: 1.5, flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
                        {metadata?.creditsUsed != null ? `Credits used: ${metadata.creditsUsed}` : ''}
                    </Typography>
                    <Button onClick={exportToPDF} startIcon={<PdfIcon />} size="small" sx={{ textTransform: 'none' }}>Export PDF</Button>
                    <Button onClick={onClose} sx={{ textTransform: 'none' }}>Close</Button>
                </DialogActions>
            )}
        </Dialog>
    );
};

export default PortfolioAnalysisModal;